import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";
import { MatTooltipModule } from "@angular/material/tooltip";
import { RouterLink } from "@angular/router";

import { ProductType } from "../../../helpers/enums/productType";
import { subscriptionTileNameKey } from "../../../helpers/subscription-i18n";
import { CombinedServiceTileInput } from "./combined-service-tile.helpers";

const ALLOWED_PRODUCT_TYPES = [
  ProductType.IP,
  ProductType.L3VPN,
  ProductType.Port,
  ProductType.L2VPN,
  ProductType.IPPrefixes,
  ProductType.LightPath,
];

@Component({
  selector: "combined-service-tile",
  templateUrl: "combined-service-tile.html",
  styleUrls: ["combined-service-tile.scss", "../service-tile/service-tile.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, CommonModule, MatTooltipModule, RouterLink, TranslatePipe],
})
export class CombinedServiceTileComponent {
  protected subscriptions = input.required<Map<ProductType, CombinedServiceTileInput>>();
  protected readonly tileNameKey = subscriptionTileNameKey;
  public subscriptionRoutes = computed(() => {
    // Returns a map, where the values are internal routes or external links. If the
    // value is an array of strings it is an internal route, otherwise it is an external
    // link. They should be consumed using [routerLink] and [href], respectively.
    const map: { [key: string]: string[] | string } = {};
    this.subscriptions().forEach((subscription, productType) => {
      if (subscription.url) map[productType] = subscription.url;
      else if (productType === ProductType.IPPrefixes) map[productType] = ["/ip-prefixes"];
      else if (productType === undefined || productType === null) map[productType] = [];
      else map[productType] = ["/diensten", productType];
    });
    return map;
  });

  private withoutIpPrefix = computed(() =>
    // we don't include the IP prefix in the count, as it can't report on traffic status
    ALLOWED_PRODUCT_TYPES.filter((type) => type !== ProductType.IPPrefixes)
      .map((type) => this.subscriptions().get(type)?.counts)
      .filter((counts) => counts),
  );

  protected okCounts = computed(() => this.withoutIpPrefix().reduce((acc, counts) => acc + (counts?.ok ?? 0), 0));
  protected notrafficCounts = computed(() =>
    this.withoutIpPrefix().reduce((acc, counts) => acc + (counts?.notraffic ?? 0), 0),
  );
  protected malfunctionCounts = computed(() =>
    this.withoutIpPrefix().reduce((acc, counts) => acc + (counts?.malfunction ?? 0), 0),
  );

  protected servicesCount = computed(() =>
    ALLOWED_PRODUCT_TYPES.map((type) => this.subscriptions().get(type))
      .filter((productSubscription) => productSubscription && typeof productSubscription.subscriptions === "number")
      .reduce((acc, productSubscription) => acc + (productSubscription?.subscriptions ?? 0), 0),
  );

  protected noServices = computed(() => this.servicesCount() === 0);
}
